import { EXTERNAL_SIGNER_VALUE } from "./configure_signers_helpers";
import {
    build_signer_slots,
    can_swap_slots,
    get_slot_step_id,
    get_slot_swap_index,
    get_sorted_step_slots,
    get_step_internal_capacity,
    get_unique_step_ids,
    has_selected_user_id,
    is_internal_slot,
    swap_slots,
    sync_single_slot_source_type
} from "./configure_signers_slot_support";

function format_slot_initials(label) {
    const words = String(label || "?")
        .trim()
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, 2);

    if (!words.length) {
        return "?";
    }

    const initials = words.map(function map_initial(word) {
        return word.charAt(0).toUpperCase();
    }).join("");
    return initials;
}

function get_selected_user_ids(step_slots) {
    const selected_user_ids = step_slots
        .filter(function filter_internal_slots(candidate) {
            return is_internal_slot(candidate);
        })
        .filter(function filter_selected_slots(candidate) {
            return has_selected_user_id(candidate);
        })
        .map(function map_selected_user_id(candidate) {
            return String(candidate.selected_user_id);
        });
    return selected_user_ids;
}

function reset_external_slot(vm, slot) {
    slot.source_type = "external";
    slot.selected_user_id = "";
    slot.email = "";
    slot.external_name = "";
    sync_slot_source_types(vm);
}

function update_internal_slot(vm, slot) {
    slot.source_type = "internal";
    const matched_user = get_user_by_id(slot, slot.selected_user_id);
    slot.email = matched_user?.email || slot.email || "";
    slot.external_name = "";
    sync_slot_source_types(vm);
}

export { build_signer_slots };

export const get_slots_for_step = (vm, step_id) => {
    const signer_slots = Array.isArray(vm.signer_slots) ? vm.signer_slots : [];
    const step_slots = signer_slots.filter(function filter_step_slots(slot) {
        return get_slot_step_id(slot) === step_id;
    });
    return step_slots;
};

export const get_internal_slot_capacity = (vm, slot) => {
    const available_count = Array.isArray(slot?.user_options) ? slot.user_options.length : 0;
    const step_slots = get_slots_for_step(vm, slot?.step_id);
    return Math.min(step_slots.length, available_count);
};

export const sync_slot_source_types = (vm) => {
    const step_ids = get_unique_step_ids(vm.signer_slots);

    step_ids.forEach(function sync_step_slots(step_id) {
        const step_slots = get_slots_for_step(vm, step_id);
        const sorted_step_slots = get_sorted_step_slots(step_slots);
        const internal_capacity = get_step_internal_capacity(sorted_step_slots);

        sorted_step_slots.forEach(function sync_slot(slot, index) {
            sync_single_slot_source_type(slot, index, internal_capacity);
        });
    });
};

export const get_role_label = (slot) => {
    return String(slot?.role_name || "signer").trim() || "signer";
};

export const get_role_label_lower = (slot) => {
    return get_role_label(slot).toLowerCase();
};

export const format_user_label = (user) => {
    const full_name = [user?.first_name, user?.last_name].filter(Boolean).join(" ").trim();
    return full_name || user?.email || "Selected user";
};

export const get_user_by_id = (slot, user_id) => {
    const user_options = Array.isArray(slot?.user_options) ? slot.user_options : [];
    const matched_user = user_options.find(function find_user(user) {
        return String(user.user_id) === String(user_id);
    }) || null;
    return matched_user;
};

function get_internal_slot_display_name(slot) {
    const matched_user = get_user_by_id(slot, slot.selected_user_id);
    if (!matched_user) {
        return get_role_label(slot);
    }

    return format_user_label(matched_user);
}

export const get_slot_display_name = (slot) => {
    if (!slot) {
        return "Signer";
    }

    if (slot.source_type === "external") {
        return slot.external_name || `External ${get_role_label(slot)}`;
    }

    return get_internal_slot_display_name(slot);
};

export const get_slot_initials = (slot) => {
    const label = get_slot_display_name(slot);
    const initials = format_slot_initials(label);
    return initials;
};

export const get_slot_badge = (slot) => {
    return slot?.source_type === "external" ? "External" : `From ${get_role_label(slot)}`;
};

export const get_selected_user_ids_for_step = (vm, slot) => {
    if (!slot?.step_id) {
        return [];
    }

    const step_slots = get_slots_for_step(vm, slot.step_id);
    const selected_user_ids = get_selected_user_ids(step_slots);
    return selected_user_ids;
};

function is_empty_candidate_user_id(candidate_user_id) {
    return !candidate_user_id;
}

function is_current_selected_user(slot, candidate_user_id) {
    const current_selected_user_id = String(slot?.selected_user_id || "");
    return candidate_user_id === current_selected_user_id;
}

export const is_user_option_disabled = (vm, slot, user) => {
    const candidate_user_id = String(user?.user_id || "");
    if (is_empty_candidate_user_id(candidate_user_id)) {
        return false;
    }

    if (is_current_selected_user(slot, candidate_user_id)) {
        return false;
    }

    const is_disabled = get_selected_user_ids_for_step(vm, slot).includes(candidate_user_id);
    return is_disabled;
};

export const get_available_user_options = (vm, slot) => {
    const options = Array.isArray(slot?.user_options) ? slot.user_options : [];
    const available_options = options.filter(function filter_available_users(user) {
        return !is_user_option_disabled(vm, slot, user);
    });
    return available_options;
};

export const should_show_internal_selector = (vm, slot) => {
    if (slot?.source_type !== "internal") {
        return false;
    }

    if (String(slot?.selected_user_id || "").trim()) {
        return true;
    }

    return get_available_user_options(vm, slot).length > 0;
};

export const on_user_selection_change = (vm, slot) => {
    if (!slot) {
        return;
    }

    if (slot.selected_user_id === EXTERNAL_SIGNER_VALUE) {
        reset_external_slot(vm, slot);
        return;
    }

    update_internal_slot(vm, slot);
};

export const switch_slot_to_internal = (vm, slot) => {
    if (!slot || !get_available_user_options(vm, slot).length) {
        return;
    }

    slot.source_type = "internal";
    slot.external_name = "";
    slot.email = "";
};

export const move_slot = (vm, index, direction) => {
    if (!vm.has_real_signer_slots) {
        return;
    }

    const swap_index = get_slot_swap_index(index, direction);
    if (!can_swap_slots(vm.signer_slots, index, swap_index)) {
        return;
    }

    vm.signer_slots = swap_slots(vm.signer_slots, index, swap_index);
    sync_slot_source_types(vm);
};

export const can_move_slot = (vm, index, direction) => {
    if (!vm.has_real_signer_slots) {
        return false;
    }

    const swap_index = get_slot_swap_index(index, direction);
    return can_swap_slots(vm.signer_slots, index, swap_index);
};
